/**
 * Native runtime bootstrap for the Capacitor-wrapped mobile build.
 *
 * SAFE in the web build too — every call is guarded by `Capacitor.isNativePlatform()`
 * so the web bundle behaves identically to before the Capacitor install.
 *
 * Responsibilities:
 *  - Hide the splash screen once React has mounted (so users don't stare at
 *    the brand splash for the full 2 s if startup is fast).
 *  - Apply the correct status-bar style based on the current theme.
 *  - Wire the Android hardware back button to browser-history navigation.
 *  - Listen for app-resume so we can re-validate the auth cookie if it expired
 *    while the app was in the background.
 */
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

export const isNative = () => Capacitor.isNativePlatform();

let initialized = false;

export async function initNativeRuntime({ onAppResume } = {}) {
  if (initialized || !isNative()) return;
  initialized = true;

  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (e) {
    // Plugin not available on this surface; ignore.
  }

  try {
    // Default to dark status-bar text on light backgrounds; web theme provider
    // can call `setStatusBarTheme()` later to override.
    await StatusBar.setStyle({ style: Style.Default });
  } catch (e) {
    // ignore
  }

  // Hardware back button (Android): go back in history; exit if at root.
  try {
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapacitorApp.exitApp();
      }
    });
  } catch (e) {
    // ignore
  }

  // App resume from background — useful for cookie re-validation.
  if (typeof onAppResume === 'function') {
    try {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) onAppResume();
      });
    } catch (e) {
      // ignore
    }
  }
}

/**
 * Switch status-bar style when the user toggles light/dark theme.
 * Called by ThemeProvider; safely no-ops on web.
 */
export async function setStatusBarTheme(theme) {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({
      style: theme === 'dark' ? Style.Dark : Style.Light,
    });
  } catch (e) {
    // ignore
  }
}
